/** Hook that encapsulates Drag-and-Drop logic for the PlanPage. */
import { useState } from 'react';
import {
  useSensors,
  useSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  closestCenter,
} from '@dnd-kit/core';
import { CustomPointerSensor } from '@/sensors/CustomPointerSensor';
import { useTaskDragAndDrop } from '@/features/tasks/hooks/list/use-list-dnd.js';
import {
  createDropHandlers,
  createRouteDropHandler,
} from '@/features/tasks/utils/interactions/dragHandlers.js';
import {
  applyPlanPageDragEnd,
  isGridDropTarget,
} from '@/features/planning/hooks/planPageDndHelpers';
import { detectTouchDevice } from '@/hooks/ui/detectTouchDevice';

/** Prefer calendar droppables when pointer hits grid and panel zones together. */
const preferGridCollisions = (collisions) => {
  if (!collisions?.length) return collisions;
  const gridHits = collisions.filter((collision) => {
    const data = collision.data?.current ?? collision.data;
    return isGridDropTarget(data);
  });
  return gridHits.length > 0 ? gridHits : collisions;
};

export const usePlanPageDnd = (
  allTasks,
  unscheduledTasks,
  tasksMap,
  user,
  updateTask,
  handleToggleComplete,
  currentTab,
  planMode = false,
  planRailTasks = [],
  moveTask,
) => {
  const isTouchDevice = detectTouchDevice();
  const {
    activeItem,
    handleDragStart: dndHandleDragStart,
    handleDragCancel: dndHandleDragCancel,
  } = useTaskDragAndDrop(allTasks);
  const [isOverGrid, setIsOverGrid] = useState(false);
  const [overGridWidth, setOverGridWidth] = useState(null);

  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: isTouchDevice
        ? { delay: 250, tolerance: 5 }
        : { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  const handleDragStart = (event) => {
    document.body.classList.add('dnd-is-dragging');
    dndHandleDragStart(event);
  };

  const handleDragOver = (event) => {
    const overGrid = isGridDropTarget(event.over?.data?.current);
    setIsOverGrid(overGrid);
    setOverGridWidth(overGrid ? (event.over?.rect?.width ?? null) : null);
  };

  const handleDragCancel = () => {
    document.body.classList.remove('dnd-is-dragging');
    dndHandleDragCancel();
    setIsOverGrid(false);
    setOverGridWidth(null);
  };

  const {
    handleCalendarSlotDrop,
    handleUnscheduledTaskDrop,
    handleUnscheduledListDrop,
  } = createDropHandlers(
    handleToggleComplete,
    updateTask,
    user?.uid,
    tasksMap,
    unscheduledTasks,
    moveTask,
  );

  const routeDropHandler = createRouteDropHandler(
    handleCalendarSlotDrop,
    handleUnscheduledTaskDrop,
    handleUnscheduledListDrop,
  );

  const handleDragEnd = (event) => {
    document.body.classList.remove('dnd-is-dragging');
    const { active, over } = event;
    dndHandleDragCancel();
    setIsOverGrid(false);
    setOverGridWidth(null);
    applyPlanPageDragEnd({
      active,
      over,
      allTasks,
      tasksMap,
      userId: user?.uid,
      updateTask,
      moveTask,
      planMode,
      planRailTasks,
      routeDropHandler,
    });
  };

  const collisionDetection =
    currentTab === 0
      ? (args) => {
          const pointerCollisions = pointerWithin(args);
          const hits =
            pointerCollisions.length > 0
              ? pointerCollisions
              : rectIntersection(args);
          return preferGridCollisions(hits);
        }
      : closestCenter;

  return {
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    collisionDetection,
    activeItem,
    isOverGrid,
    overGridWidth,
  };
};
